import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faClock, faEye, faFileAlt, faSync, faUser } from '@fortawesome/free-solid-svg-icons'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'

// shadcn/ui components
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DualProgressBar } from '../components/ui/dual-progress-bar'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import type { DocumentType, GetTasksRequest } from '../../shared/types'
import { useTasks } from '../hooks/useHonoApi'
import { calculateTaskStats, cn, formatDate } from '../lib/utils'

// Modern Statistic component for shadcn/ui
interface StatisticProps {
	title: string
	value: number
	icon: React.ReactNode
	iconColor: string
	className?: string
}

const Statistic = ({ title, value, icon, iconColor, className }: StatisticProps) => (
	<div className={cn('flex items-center justify-between', className)}>
		<div className="space-y-1">
			<p className="text-sm font-medium text-muted-foreground">{title}</p>
			<p className="text-3xl font-bold tracking-tight">{value}</p>
		</div>
		<div className={cn('p-3 rounded-xl shadow-sm', iconColor)}>{icon}</div>
	</div>
)

// Helper function to get user display name
const getUserDisplayName = (user: any): string => {
	if (user?.first_name && user?.last_name) {
		return `${user.first_name} ${user.last_name}`
	}
	if (user?.first_name) {
		return user.first_name
	}
	if (user?.email) {
		return user.email
	}
	return 'Unknown User'
}

// Helper function to get user initials
const getUserInitials = (user: any): string => {
	if (user?.first_name && user?.last_name) {
		return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase()
	}
	if (user?.first_name) {
		return user.first_name.charAt(0).toUpperCase()
	}
	if (user?.email) {
		return user.email.charAt(0).toUpperCase()
	}
	return 'U'
}

export default function DashboardNew() {
	// Pagination and filter state
	const [currentPage, setCurrentPage] = useState(1)
	const [pageSize, setPageSize] = useState(10)
	const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | null>(null)

	// Build request parameters with useMemo to prevent infinite re-renders
	const requestParams = useMemo<GetTasksRequest>(
		() => ({
			page: currentPage,
			limit: pageSize,
			document_type: selectedDocumentType || undefined,
		}),
		[currentPage, pageSize, selectedDocumentType]
	)

	// Use the new HonoApi hook for tasks with server-side pagination
	const { data: tasks, loading, error, pagination, refetch: loadTasks } = useTasks(requestParams)

	// Document type options for filter
	const documentTypeOptions = [
		{ value: 'lawsuit_petition', label: 'คำฟ้อง' },
		{ value: 'enforcement_document', label: 'หมายบังคับคดี' },
		{ value: 'receipt_legal', label: 'ใบเสร็จฟ้องร้อง' },
		{ value: 'receipt_enforcement', label: 'ใบเสร็จบังคับคดี' },
		{ value: 'fee_account', label: 'บัญชีฤชา' },
		{ value: 'oral_judgment_record', label: 'คำพิพากษา' },
		{ value: 'court_fee_payment_voucher', label: 'ใบสั่งจ่ายค่าธรรมเนียมศาล รจ.2' },
		{ value: 'procedural_record', label: 'รายงานกระบวน' },
	]

	// Handle document type filter change
	const handleDocumentTypeFilter = (value: string) => {
		setSelectedDocumentType(value as DocumentType)
		setCurrentPage(1) // Reset to first page when filter changes
	}

	// Handle pagination change
	const handlePaginationChange = (page: number, size: number) => {
		setCurrentPage(page)
		if (size !== pageSize) {
			setPageSize(size)
		}
	}

	const getStatusVariant = (
		status: string
	): 'default' | 'secondary' | 'destructive' | 'outline' => {
		switch (status) {
			case 'pending':
				return 'outline'
			case 'in_review':
				return 'default'
			case 'completed':
				return 'secondary'
			default:
				return 'outline'
		}
	}

	const getStatusText = (status: string) => {
		return status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())
	}

	// For stats, we need to fetch all tasks (without pagination) to get accurate counts
	// Memoize stats parameters to prevent infinite re-renders
	const statsParams = useMemo(() => ({ limit: 1000 }), [])
	const { data: allTasksForStats } = useTasks(statsParams)
	const stats = calculateTaskStats(allTasksForStats || [])

	if (loading) {
		return (
			<div className="space-y-8">
				{/* Header Skeleton */}
				<div className="space-y-3">
					<Skeleton className="h-10 w-96" />
					<Skeleton className="h-4 w-64" />
				</div>

				{/* Stats Cards Skeleton */}
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
					{[...Array(4)].map((_, i) => (
						<Card key={i} className="border-0 shadow-md">
							<CardContent className="p-4 sm:p-6">
								<div className="flex items-center justify-between">
									<div className="space-y-3">
										<Skeleton className="h-4 w-20" />
										<Skeleton className="h-8 w-16" />
									</div>
									<Skeleton className="h-12 w-12 rounded-xl" />
								</div>
							</CardContent>
						</Card>
					))}
				</div>

				{/* Filter Skeleton */}
				<Card className="border-0 shadow-sm">
					<CardContent className="p-4 sm:p-6">
						<div className="flex items-center gap-4">
							<Skeleton className="h-4 w-32" />
							<Skeleton className="h-10 w-80" />
							<Skeleton className="h-8 w-20" />
						</div>
					</CardContent>
				</Card>

				{/* Table Skeleton */}
				<Card className="border-0 shadow-sm">
					<CardContent className="p-4 sm:p-6">
						<div className="space-y-4">
							<Skeleton className="h-6 w-40" />
							<div className="space-y-3">
								{[...Array(5)].map((_, i) => (
									<Skeleton key={i} className="h-16 w-full" />
								))}
							</div>
						</div>
					</CardContent>
				</Card>
			</div>
		)
	}

	if (error) {
		return (
			<div>
				<Alert variant="destructive">
					<AlertDescription className="flex items-center justify-between">
						<span>{error}</span>
						<Button variant="outline" size="sm" onClick={() => loadTasks()}>
							<FontAwesomeIcon icon={faSync} className="h-4 w-4 mr-2" />
							Try Again
						</Button>
					</AlertDescription>
				</Alert>
			</div>
		)
	}

	return (
		<div className="space-y-4 sm:space-y-6 lg:space-y-8 pt-2 sm:pt-4 lg:pt-6 max-w-full overflow-hidden">
			{/* Modern Header */}
			<div className="space-y-1">
				<h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
					Document Processing Dashboard
				</h1>
				<p className="text-muted-foreground">
					Monitor and manage your document processing workflow
				</p>
			</div>

			{/* Modern Stats Cards */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
				<Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-200">
					<CardContent className="p-4 sm:p-6">
						<Statistic
							title="Total Tasks"
							value={stats.total}
							icon={<FontAwesomeIcon icon={faFileAlt} className="h-6 w-6 text-white" />}
							iconColor="bg-gradient-to-br from-blue-500 to-blue-600"
						/>
					</CardContent>
				</Card>
				<Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-200">
					<CardContent className="p-4 sm:p-6">
						<Statistic
							title="Pending"
							value={stats.pending}
							icon={<FontAwesomeIcon icon={faClock} className="h-6 w-6 text-white" />}
							iconColor="bg-gradient-to-br from-amber-500 to-orange-500"
						/>
					</CardContent>
				</Card>
				<Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-200">
					<CardContent className="p-4 sm:p-6">
						<Statistic
							title="In Review"
							value={stats.in_review}
							icon={<FontAwesomeIcon icon={faEye} className="h-6 w-6 text-white" />}
							iconColor="bg-gradient-to-br from-purple-500 to-violet-600"
						/>
					</CardContent>
				</Card>
				<Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-200">
					<CardContent className="p-4 sm:p-6">
						<Statistic
							title="Completed"
							value={stats.completed}
							icon={<FontAwesomeIcon icon={faCheckCircle} className="h-6 w-6 text-white" />}
							iconColor="bg-gradient-to-br from-emerald-500 to-green-600"
						/>
					</CardContent>
				</Card>
			</div>

			{/* Modern Tasks Table */}
			<Card className="border-0 shadow-sm">
				<CardHeader className="pb-4">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
						<div className="flex items-center gap-2">
							<div className="h-2 w-2 rounded-full bg-purple-500"></div>
							<CardTitle className="text-xl font-semibold">Recent Tasks</CardTitle>
						</div>
						<div className="flex flex-col sm:flex-row sm:items-center gap-3">
							<div className="h-3 w-3 rounded-full bg-blue-500 shrink-0"></div>
							<p className="font-semibold text-sm leading-none shrink-0">กรองตามประเภทเอกสาร</p>
							<Select
								value={selectedDocumentType || undefined}
								onValueChange={handleDocumentTypeFilter}
							>
								<SelectTrigger className="w-full sm:w-80 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20">
									<SelectValue placeholder="เลือกประเภทเอกสาร" />
								</SelectTrigger>
								<SelectContent>
									{documentTypeOptions.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{selectedDocumentType && (
								<Button
									variant="outline"
									size="sm"
									className="border-gray-200 hover:bg-gray-50"
									onClick={() => {
										setSelectedDocumentType(null)
										setCurrentPage(1)
									}}
								>
									Clear Filter
								</Button>
							)}
						</div>
					</div>
				</CardHeader>
				<CardContent className="p-4 sm:p-6">
					<div className="rounded-lg border border-gray-100 overflow-hidden">
						<div className="overflow-x-auto">
							<Table>
							<TableHeader>
								<TableRow className="bg-gray-50/50 hover:bg-gray-50/50">
									<TableHead className="font-semibold text-gray-900 text-xs sm:text-sm max-w-[200px] sm:max-w-[300px]">Task</TableHead>
									<TableHead className="font-semibold text-gray-900 text-xs sm:text-sm hidden sm:table-cell">ประเภทเอกสาร</TableHead>
									<TableHead className="font-semibold text-gray-900 text-xs sm:text-sm">Status</TableHead>
									<TableHead className="font-semibold text-gray-900 text-xs sm:text-sm hidden lg:table-cell">Progress</TableHead>
									<TableHead className="font-semibold text-gray-900 text-xs sm:text-sm hidden md:table-cell">Uploaded By</TableHead>
									<TableHead className="font-semibold text-gray-900 text-xs sm:text-sm hidden lg:table-cell">Created</TableHead>
									<TableHead className="font-semibold text-gray-900 text-xs sm:text-sm w-20">Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{tasks && tasks.length > 0 ? (
									tasks.map((task) => {
										return (
											<TableRow key={task.id} className="hover:bg-gray-50/50 transition-colors">
												<TableCell className="py-2 sm:py-4 max-w-[200px] sm:max-w-[300px]">
													<div className="space-y-1">
														<div className="font-semibold text-gray-900 text-sm sm:text-base truncate">{task.name}</div>
														<div className="text-xs sm:text-sm text-gray-500 truncate">{task.description}</div>
														{/* Show document type on mobile when column is hidden */}
														<div className="sm:hidden flex flex-wrap gap-1 mt-1">
															<Badge
																variant="outline"
																className="bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border-blue-200 font-medium text-xs"
															>
																{task.document_type_thai}
															</Badge>
															{/* Show progress on mobile when hidden */}
															<span className="lg:hidden text-xs text-gray-500">
																{task.approved_documents}✓ {task.rejected_documents}✗ / {task.total_documents}
															</span>
															{/* Show created date on mobile when hidden */}
															<span className="lg:hidden text-xs text-gray-500">
																{formatDate(task.created_at)}
															</span>
														</div>
													</div>
												</TableCell>
												<TableCell className="py-2 sm:py-4 hidden sm:table-cell">
													<Badge
														variant="outline"
														className="bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border-blue-200 font-medium"
													>
														{task.document_type_thai}
													</Badge>
												</TableCell>
												<TableCell className="py-2 sm:py-4">
													<Badge
														variant={getStatusVariant(task.status)}
														className={cn(
															'font-medium text-xs',
															task.status === 'pending' &&
																'bg-amber-50 text-amber-700 border-amber-200',
															task.status === 'in_review' &&
																'bg-blue-50 text-blue-700 border-blue-200',
															task.status === 'completed' &&
																'bg-emerald-50 text-emerald-700 border-emerald-200'
														)}
													>
														{getStatusText(task.status)}
													</Badge>
												</TableCell>
												<TableCell className="py-2 sm:py-4 hidden lg:table-cell">
													<div className="w-32 sm:w-40">
														<DualProgressBar
															approved={task.approved_documents}
															rejected={task.rejected_documents}
															total={task.total_documents}
															showLabel={false}
															showPercentage={false}
															height="h-2"
														/>
														<div className="text-xs text-gray-600 mt-1">
															{task.approved_documents + task.rejected_documents}/{task.total_documents}
														</div>
													</div>
												</TableCell>
												<TableCell className="py-2 sm:py-4 hidden md:table-cell">
													{task.user ? (
														<div className="flex items-center gap-2">
															<Avatar className="h-6 w-6 sm:h-8 sm:w-8">
																<AvatarImage
																	src={task.user.image_url || undefined}
																	alt={getUserDisplayName(task.user)}
																/>
																<AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-xs font-medium">
																	{getUserInitials(task.user)}
																</AvatarFallback>
															</Avatar>
															<div className="space-y-1 min-w-0">
																<div className="text-xs sm:text-sm font-medium text-gray-900 truncate">
																	{getUserDisplayName(task.user)}
																</div>
																{task.user.email && (
																	<div className="text-xs text-gray-500 truncate">{task.user.email}</div>
																)}
															</div>
														</div>
													) : (
														<div className="flex items-center gap-2">
															<Avatar className="h-6 w-6 sm:h-8 sm:w-8">
																<AvatarFallback className="bg-gray-300 text-gray-600">
																	<FontAwesomeIcon icon={faUser} className="h-3 w-3 sm:h-4 sm:w-4" />
																</AvatarFallback>
															</Avatar>
															<div className="text-xs sm:text-sm text-gray-500">Unassigned</div>
														</div>
													)}
												</TableCell>
												<TableCell className="py-2 sm:py-4 text-xs sm:text-sm text-gray-600 hidden lg:table-cell">
													{formatDate(task.created_at)}
												</TableCell>
												<TableCell className="py-2 sm:py-4 w-20">
													<Button
														variant="ghost"
														size="sm"
														asChild
														className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0 sm:h-auto sm:w-auto sm:p-2"
													>
														<Link to={`/task/${task.id}`} className="flex items-center">
															<FontAwesomeIcon icon={faEye} className="h-4 w-4 sm:mr-2" />
															<span className="hidden sm:inline">View Details</span>
														</Link>
													</Button>
												</TableCell>
											</TableRow>
										)
									})
								) : (
									<TableRow>
										<TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
											{selectedDocumentType ? 'ไม่พบงานประเภทนี้' : 'No tasks found'}
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
						</div>
					</div>

					{/* Modern Pagination */}
					{pagination && pagination.total > 0 && (
						<div className="flex items-center justify-between pt-6 border-t border-gray-100">
							<p className="text-sm text-gray-600 font-medium">
								Showing {(currentPage - 1) * pageSize + 1} to{' '}
								{Math.min(currentPage * pageSize, pagination.total)} of {pagination.total} tasks
							</p>
							<div className="flex items-center space-x-3">
								<Button
									variant="outline"
									size="sm"
									className="border-gray-200 hover:bg-gray-50"
									onClick={() => handlePaginationChange(currentPage - 1, pageSize)}
									disabled={currentPage <= 1}
								>
									Previous
								</Button>
								<div className="flex items-center space-x-1">
									<span className="text-sm font-medium text-gray-600">
										Page {currentPage} of {Math.ceil(pagination.total / pageSize)}
									</span>
								</div>
								<Button
									variant="outline"
									size="sm"
									className="border-gray-200 hover:bg-gray-50"
									onClick={() => handlePaginationChange(currentPage + 1, pageSize)}
									disabled={currentPage >= Math.ceil(pagination.total / pageSize)}
								>
									Next
								</Button>
							</div>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	)
}
